import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';

const ExamCellSemesterFees = ({ onNavigate, userInfo }) => {
    // ====================================================================================================
    // STATE MANAGEMENT
    // ====================================================================================================
    const [isMobile, setIsMobile] = useState(false);
    const [loading, setLoading] = useState(true);

    // Filters
    const [selectedCourse, setSelectedCourse] = useState('All');
    const [selectedSemester, setSelectedSemester] = useState('All');
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');

    // Modal
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

    // ====================================================================================================
    // MOCK DATA
    // ====================================================================================================
    // Fee Structures
    const feeStructure = {
        'BCA': 45000,
        'MCA': 60000,
        'MBA': 75000
    };

    // Student Data with Fee Records
    const [studentFees, setStudentFees] = useState([
        {
            id: 'STU001',
            name: 'Rahul Kumar',
            course: 'BCA',
            semester: '3rd',
            rollNo: '24BCA001',
            totalFees: 45000,
            paidFees: 45000,
            pendingFees: 0,
            status: 'Paid',
            lastPaymentDate: '2026-01-05',
            history: [
                { date: '2026-01-05', amount: 20000, mode: 'Online', ref: 'TXN123456' },
                { date: '2025-08-10', amount: 25000, mode: 'Bank Transfer', ref: 'UTR987654' }
            ]
        },
        {
            id: 'STU002',
            name: 'Priya Sharma',
            course: 'BCA',
            semester: '3rd',
            rollNo: '24BCA002',
            totalFees: 45000,
            paidFees: 20000,
            pendingFees: 25000,
            status: 'Partial',
            lastPaymentDate: '2025-12-15',
            history: [
                { date: '2025-12-15', amount: 20000, mode: 'Online', ref: 'TXN789012' }
            ]
        },
        {
            id: 'STU003',
            name: 'Amit Patel',
            course: 'MCA',
            semester: '1st',
            rollNo: '25MCA001',
            totalFees: 60000,
            paidFees: 0,
            pendingFees: 60000,
            status: 'Pending',
            lastPaymentDate: '-',
            history: []
        },
        {
            id: 'STU004',
            name: 'Sneha Gupta',
            course: 'BCA',
            semester: '5th',
            rollNo: '23BCA001',
            totalFees: 45000,
            paidFees: 45000,
            pendingFees: 0,
            status: 'Paid',
            lastPaymentDate: '2026-01-10',
            history: [
                { date: '2026-01-10', amount: 45000, mode: 'Cheque', ref: 'CHQ456789' }
            ]
        },
        // Generating more Mock Data
        ...Array.from({ length: 25 }).map((_, i) => {
            const course = i % 3 === 0 ? 'BCA' : i % 3 === 1 ? 'MCA' : 'MBA';
            const total = feeStructure[course];
            const statusType = i % 5 === 0 ? 'Pending' : i % 5 === 1 ? 'Partial' : 'Paid';
            const paid = statusType === 'Pending' ? 0 : statusType === 'Paid' ? total : total / 2;

            return {
                id: `STU0${i + 10}`,
                name: `Student Name ${i + 1}`,
                course: course,
                semester: `${(i % 6) + 1}th`,
                rollNo: `${24 + (i % 2)}${course}00${i + 1}`,
                totalFees: total,
                paidFees: paid,
                pendingFees: total - paid,
                status: statusType,
                lastPaymentDate: statusType === 'Pending' ? '-' : `2026-01-${(i % 15) + 1}`,
                history: statusType !== 'Pending' ? [
                    { date: `2026-01-${(i % 15) + 1}`, amount: paid, mode: 'Online', ref: `TXN${1000 + i}` }
                ] : []
            };
        })
    ]);

    // ====================================================================================================
    // EFFECTS & HELPERS
    // ====================================================================================================
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        setTimeout(() => setLoading(false), 800);
    }, []);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
    };

    // ====================================================================================================
    // FILTERING
    // ====================================================================================================
    const filteredData = studentFees.filter(student => {
        const matchesSearch =
            student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            student.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
            student.rollNo.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesCourse = selectedCourse === 'All' || student.course === selectedCourse;
        const matchesSemester = selectedSemester === 'All' || student.semester === selectedSemester;
        const matchesStatus = selectedStatus === 'All' || student.status === selectedStatus;

        return matchesSearch && matchesCourse && matchesSemester && matchesStatus;
    });

    const totalCollected = filteredData.reduce((sum, item) => sum + item.paidFees, 0);
    const totalPending = filteredData.reduce((sum, item) => sum + item.pendingFees, 0);
    const completionRate = (totalCollected / (totalCollected + totalPending)) * 100;

    // ====================================================================================================
    // UI COMPONENTS
    // ====================================================================================================

    const HistoryModal = () => {
        if (!selectedStudent) return null;
        return (
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.6)', zIndex: 1100,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backdropFilter: 'blur(3px)'
            }}>
                <div style={{
                    background: '#fff', width: '100%', maxWidth: '600px',
                    borderRadius: '16px', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                }}>
                    <div style={{ padding: '1.5rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                        <h3 style={{ margin: 0, color: '#2d3748' }}>Payment History</h3>
                        <button onClick={() => { setIsHistoryModalOpen(false); setSelectedStudent(null); }} style={{ border: 'none', background: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#718096' }}>✕</button>
                    </div>
                    <div style={{ padding: '2rem' }}>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{ margin: '0 0 0.5rem 0', color: '#238548', fontSize: '1.2rem' }}>{selectedStudent.name}</h4>
                            <p style={{ margin: 0, color: '#718096', fontSize: '0.9rem' }}>{selectedStudent.course} - {selectedStudent.semester} ({selectedStudent.rollNo})</p>
                        </div>

                        {selectedStudent.history.length > 0 ? (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ background: '#edf2f7' }}>
                                    <tr>
                                        <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.9rem', color: '#4a5568' }}>Date</th>
                                        <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.9rem', color: '#4a5568' }}>Reference ID</th>
                                        <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.9rem', color: '#4a5568' }}>Mode</th>
                                        <th style={{ textAlign: 'right', padding: '0.75rem', fontSize: '0.9rem', color: '#4a5568' }}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedStudent.history.map((txn, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '0.75rem', fontSize: '0.9rem' }}>{txn.date}</td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.9rem', fontFamily: 'monospace' }}>{txn.ref}</td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.9rem' }}>{txn.mode}</td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.9rem', textAlign: 'right', fontWeight: '600' }}>{formatCurrency(txn.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p style={{ color: '#718096', textAlign: 'center', padding: '1rem', background: '#f7fafc', borderRadius: '8px' }}>No payment history found.</p>
                        )}

                        <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '2px dashed #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#4a5568', fontWeight: '500' }}>Total Paid:</span>
                            <span style={{ color: '#238548', fontWeight: '700' }}>{formatCurrency(selectedStudent.paidFees)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                            <span style={{ color: '#4a5568', fontWeight: '500' }}>Pending Due:</span>
                            <span style={{ color: '#e53e3e', fontWeight: '700' }}>{formatCurrency(selectedStudent.pendingFees)}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ====================================================================================================
    // MAIN RENDER
    // ====================================================================================================
    return (
        <Layout role="examcell" onNavigate={onNavigate} userInfo={userInfo} activePage="examcell-semesterfees">
            <div style={{ marginBottom: '2rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.8rem', color: '#2d3748' }}>Semester Fees Tracking</h2>
                <p style={{ margin: '0.5rem 0 0', color: '#718096' }}>Monitor fee collections and student dues</p>
            </div>

            {/* Stats Overview */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ background: '#fff', padding: '2rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden', borderTop: '4px solid #238548' }}>
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <p style={{ color: '#718096', margin: '0 0 0.5rem 0' }}>Total Collected (This Sem)</p>
                        <h2 style={{ fontSize: '2rem', color: '#238548', margin: 0 }}>{formatCurrency(totalCollected)}</h2>
                    </div>
                    <div style={{ position: 'absolute', right: '-10px', top: '-10px', fontSize: '5rem', opacity: 0.1 }}>💰</div>
                </div>

                <div style={{ background: '#fff', padding: '2rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden', borderTop: '4px solid #e53e3e' }}>
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <p style={{ color: '#718096', margin: '0 0 0.5rem 0' }}>Outstanding Dues</p>
                        <h2 style={{ fontSize: '2rem', color: '#e53e3e', margin: 0 }}>{formatCurrency(totalPending)}</h2>
                    </div>
                    <div style={{ position: 'absolute', right: '-10px', top: '-10px', fontSize: '5rem', opacity: 0.1 }}>⚠️</div>
                </div>

                <div style={{ background: '#fff', padding: '2rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden', borderTop: '4px solid #4a5568' }}>
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <p style={{ color: '#718096', margin: '0 0 0.5rem 0' }}>Collection Progress</p>
                        <h2 style={{ fontSize: '2rem', color: '#4a5568', margin: 0 }}>{completionRate.toFixed(1)}%</h2>
                        <div style={{ height: '6px', background: '#edf2f7', borderRadius: '3px', marginTop: '1rem' }}>
                            <div style={{ height: '100%', borderRadius: '3px', background: 'linear-gradient(90deg, #238548, #97d6ac)', width: `${completionRate}%` }}></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', marginBottom: '2rem', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                    <div>
                        <label style={{ display: 'block', color: '#4a5568', fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: '500' }}>Search Student</label>
                        <input
                            type="text" placeholder="Name, ID or Roll No..."
                            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0',
                                fontSize: '0.95rem', outline: 'none', transition: 'border-color 0.2s',
                                boxSizing: 'border-box'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#238548'}
                            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', color: '#4a5568', fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: '500' }}>Course</label>
                        <select
                            value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)}
                            style={{
                                width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0',
                                fontSize: '0.95rem', outline: 'none', cursor: 'pointer', boxSizing: 'border-box'
                            }}
                        >
                            <option value="All">All Courses</option>
                            <option value="BCA">BCA</option>
                            <option value="MCA">MCA</option>
                            <option value="MBA">MBA</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', color: '#4a5568', fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: '500' }}>Semester</label>
                        <select
                            value={selectedSemester} onChange={(e) => setSelectedSemester(e.target.value)}
                            style={{
                                width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0',
                                fontSize: '0.95rem', outline: 'none', cursor: 'pointer', boxSizing: 'border-box'
                            }}
                        >
                            <option value="All">All Semesters</option>
                            <option value="1st">1st Semester</option>
                            <option value="3rd">3rd Semester</option>
                            <option value="5th">5th Semester</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', color: '#4a5568', fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: '500' }}>Payment Status</label>
                        <select
                            value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}
                            style={{
                                width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0',
                                fontSize: '0.95rem', outline: 'none', cursor: 'pointer', boxSizing: 'border-box'
                            }}
                        >
                            <option value="All">All Statuses</option>
                            <option value="Paid">Fully Paid</option>
                            <option value="Partial">Partially Paid</option>
                            <option value="Pending">Pending</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
                <div style={{ padding: '1.5rem', borderBottom: '1px solid #edf2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, color: '#2d3748', fontSize: '1.1rem' }}>Student Fee Records</h3>
                    <button style={{
                        background: '#238548', color: '#fff', border: 'none', padding: '0.6rem 1.2rem',
                        borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500',
                        boxShadow: '0 2px 5px rgba(35, 133, 72, 0.3)'
                    }}>
                        📥 Export Report
                    </button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#f8fafc' }}>
                            <tr>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontWeight: '600', fontSize: '0.85rem', textTransform: 'uppercase' }}>Student</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontWeight: '600', fontSize: '0.85rem', textTransform: 'uppercase' }}>Class</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'right', color: '#64748b', fontWeight: '600', fontSize: '0.85rem', textTransform: 'uppercase' }}>Total Fees</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'right', color: '#64748b', fontWeight: '600', fontSize: '0.85rem', textTransform: 'uppercase' }}>Paid</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'right', color: '#64748b', fontWeight: '600', fontSize: '0.85rem', textTransform: 'uppercase' }}>Balance</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'center', color: '#64748b', fontWeight: '600', fontSize: '0.85rem', textTransform: 'uppercase' }}>Status</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'center', color: '#64748b', fontWeight: '600', fontSize: '0.85rem', textTransform: 'uppercase' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" style={{ padding: '3rem', textAlign: 'center', color: '#666' }}>Loading Fee Data...</td></tr>
                            ) : filteredData.length === 0 ? (
                                <tr><td colSpan="7" style={{ padding: '3rem', textAlign: 'center', color: '#718096' }}>No records found matching filters.</td></tr>
                            ) : (
                                filteredData.map(item => (
                                    <tr key={item.id} style={{ borderBottom: '1px solid #edf2f7', transition: 'background 0.2s' }}>
                                        <td style={{ padding: '1rem 1.5rem' }}>
                                            <div style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.95rem' }}>{item.name}</div>
                                            <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.2rem' }}>{item.rollNo}</div>
                                        </td>
                                        <td style={{ padding: '1rem 1.5rem', color: '#4a5568', fontSize: '0.9rem' }}>{item.course} - {item.semester}</td>
                                        <td style={{ padding: '1rem 1.5rem', textAlign: 'right', fontWeight: '500', fontSize: '0.9rem' }}>{formatCurrency(item.totalFees)}</td>
                                        <td style={{ padding: '1rem 1.5rem', textAlign: 'right', color: '#238548', fontSize: '0.9rem' }}>{formatCurrency(item.paidFees)}</td>
                                        <td style={{ padding: '1rem 1.5rem', textAlign: 'right', color: '#e53e3e', fontWeight: '600', fontSize: '0.9rem' }}>{formatCurrency(item.pendingFees)}</td>
                                        <td style={{ padding: '1rem 1.5rem', textAlign: 'center' }}>
                                            <span style={{
                                                padding: '0.3rem 0.8rem', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 'bold',
                                                background: item.status === 'Paid' ? '#f0fdf4' : item.status === 'Pending' ? '#fff5f5' : '#fffaf0',
                                                color: item.status === 'Paid' ? '#238548' : item.status === 'Pending' ? '#e53e3e' : '#dd6b20',
                                                border: `1px solid ${item.status === 'Paid' ? '#bbf7d0' : item.status === 'Pending' ? '#fed7d7' : '#fbd38d'}`
                                            }}>
                                                {item.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '1rem 1.5rem', textAlign: 'center' }}>
                                            <button
                                                onClick={() => { setSelectedStudent(item); setIsHistoryModalOpen(true); }}
                                                style={{
                                                    background: '#edf2f7', border: 'none', padding: '0.5rem 1rem',
                                                    borderRadius: '6px', cursor: 'pointer', color: '#2d3748',
                                                    fontSize: '0.85rem', fontWeight: '500', transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.background = '#edf2f7'; }}
                                            >
                                                History
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {isHistoryModalOpen && <HistoryModal />}
        </Layout>
    );
};

export default ExamCellSemesterFees;
